import { type ReactNode } from 'react';

export interface CurrencyFormatOptions {
  /** ISO 4217 currency code. Defaults to USD for now while the app is single-currency. */
  currency?: string;
  /** BCP 47 locale. Defaults to the runtime locale (Intl picks one). */
  locale?: string;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
}

/**
 * Format a monetary value as a localized string. Negatives use accounting
 * style (parentheses) per the `currencySign: 'accounting'` Intl option.
 *
 * Returns an empty string for null, undefined, or non-finite values so
 * callers can decide their own fallback. Use the <Currency> component
 * for JSX contexts; use this helper for chart tooltips, CSV exports, etc.
 */
export function formatCurrency(
  value: number | string | null | undefined,
  options: CurrencyFormatOptions = {},
): string {
  if (value == null) return '';
  const num = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(num)) return '';

  return new Intl.NumberFormat(options.locale, {
    style: 'currency',
    currency: options.currency ?? 'USD',
    currencySign: 'accounting',
    minimumFractionDigits: options.minimumFractionDigits ?? 2,
    maximumFractionDigits: options.maximumFractionDigits ?? 2,
  }).format(num);
}

export interface CurrencyProps extends CurrencyFormatOptions {
  value: number | string | null | undefined;
  /** Rendered when value is null/undefined/non-finite. Defaults to nothing. */
  fallback?: ReactNode;
  className?: string;
}

export function Currency({
  value,
  fallback = null,
  className,
  ...options
}: CurrencyProps) {
  const formatted = formatCurrency(value, options);
  if (formatted === '') return <>{fallback}</>;
  return <span className={className}>{formatted}</span>;
}
